import React, { useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { format } from 'date-fns';
import { BirdiTheme, fonts } from '@birdi/theme';
import { GlobalSearchResult, GlobalSearchResultType } from '@birdi/js-sdk';
import CrosshairSVG from '@birdi/icons/crosshair.svg';
import DownloadSVG from '@birdi/icons/download.svg';
import ExternalLinkSVG from '@birdi/icons/external-link.svg';

const GROUP_LABELS: Record<GlobalSearchResultType, string> = {
  map: 'Maps',
  mission: 'Missions',
  dataset: 'Datasets',
};

const GO_TO_LABELS: Record<GlobalSearchResultType, string> = {
  map: 'Go to map',
  mission: 'Go to mission',
  dataset: 'Go to map',
};

/** Created date as shown on the rest of the dashboard (e.g. "12 Aug 2026"). */
export const formatDate = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : format(date, 'd MMM yyyy');
};

/** Storage size for the pill: "1.2 GB", "340 MB", "12 KB". */
export const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  const digits = unit === 0 ? 0 : value < 10 ? 1 : 0;
  return `${value.toFixed(digits)} ${units[unit]}`;
};

/** Empty-state copy: distinguish "nothing anywhere" from "nothing here". */
export const emptyMessage = (query: string, areaFiltered: boolean) => {
  const term = query.trim();
  if (term) {
    return areaFiltered
      ? `No results for “${term}” in this area`
      : `No results for “${term}”`;
  }
  return areaFiltered
    ? 'No maps or missions in this area'
    : 'No maps or missions yet';
};

/** Per-type paging state the tiles drive (PRD: each type pages itself). */
export interface TilePaging {
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}

const isPagedType = (key: string): key is GlobalSearchResultType =>
  key === 'map' || key === 'mission' || key === 'dataset';

export interface ResultTile {
  key: string;
  title: string;
  /** Rows with their index into the flat `ordered` list. */
  rows: { result: GlobalSearchResult; index: number }[];
}

/**
 * Lay the results out as paper tiles — Maps, then (while a map is selected)
 * Selected Map Datasets, then Missions, then Datasets — and return the flat
 * list in that same order so keyboard navigation follows what is on screen.
 * A dataset can appear in both dataset tiles; the flat index tells them apart.
 */
export const layoutTiles = (
  results: Record<GlobalSearchResultType, GlobalSearchResult[]>,
  mapDatasets: GlobalSearchResult[],
  selectedMapName: string | null,
): { ordered: GlobalSearchResult[]; tiles: ResultTile[] } => {
  const byType = (type: GlobalSearchResultType) => results[type];
  const groups: { key: string; title: string; items: GlobalSearchResult[] }[] =
    [
      { key: 'map', title: GROUP_LABELS.map, items: byType('map') },
      ...(selectedMapName !== null
        ? [
            {
              key: 'map-datasets',
              title: `Selected Map Datasets · ${selectedMapName}`,
              items: mapDatasets,
            },
          ]
        : []),
      { key: 'mission', title: GROUP_LABELS.mission, items: byType('mission') },
      { key: 'dataset', title: GROUP_LABELS.dataset, items: byType('dataset') },
    ];
  const ordered: GlobalSearchResult[] = [];
  const tiles: ResultTile[] = [];
  for (const group of groups) {
    const rows = group.items.map((result) => {
      ordered.push(result);
      return { result, index: ordered.length - 1 };
    });
    // The selected-map tile stays visible while empty so the user sees the
    // map has no datasets (or that they are still loading).
    if (rows.length > 0 || group.key === 'map-datasets') {
      tiles.push({ key: group.key, title: group.title, rows });
    }
  }
  return { ordered, tiles };
};

export interface ResultsListProps {
  /** Tiles from `layoutTiles`; `ordered` is its flat list, same order. */
  tiles: ResultTile[];
  ordered: GlobalSearchResult[];
  /** Still fetching the selected map's datasets. */
  mapDatasetsLoading?: boolean;
  selectedIndex: number;
  loading: boolean;
  query: string;
  /** A viewport (area) filter is active — empty states say so. */
  areaFiltered?: boolean;
  /** Paging per result type; each tile pulls its next page in on scroll. */
  paging: Record<GlobalSearchResultType, TilePaging>;
  onHighlight: (index: number) => void;
  /** Row click/enter: select + zoom the globe (activate again to open). */
  onActivate: (result: GlobalSearchResult) => void;
  /** Zoom-to icon: select the result so the globe flies to it. */
  onZoomTo: (result: GlobalSearchResult) => void;
  /** Datasets only — maps and missions link out instead. */
  onDownload: (result: GlobalSearchResult) => void;
  /** Explicit navigation: the map, the mission, or a dataset's parent map. */
  onGoToMap: (result: GlobalSearchResult) => void;
}

export const ResultsList = ({
  tiles,
  ordered,
  mapDatasetsLoading = false,
  selectedIndex,
  loading,
  query,
  areaFiltered = false,
  paging,
  onHighlight,
  onActivate,
  onZoomTo,
  onDownload,
  onGoToMap,
}: ResultsListProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Infinite scroll per tile: pull that type's next page in before the user
  // hits the end of its tile.
  const onTileScroll =
    (type: GlobalSearchResultType) => (event: React.UIEvent<HTMLElement>) => {
      const el = event.currentTarget;
      const page = paging[type];
      if (!page.hasMore || page.loading || page.loadingMore) return;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
        page.onLoadMore();
      }
    };

  // Keep the keyboard selection visible while arrowing.
  useEffect(() => {
    const el = scrollRef.current?.querySelector(
      `[data-index="${selectedIndex}"]`,
    );
    // Optional call: jsdom (unit tests) doesn't implement scrollIntoView.
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedIndex]);

  if (!loading && ordered.length === 0 && tiles.length === 0) {
    return (
      <div css={[tileCss, emptyCss]} data-testid="global-search-empty">
        {emptyMessage(query, areaFiltered)}
      </div>
    );
  }

  return (
    <div css={columnCss} ref={scrollRef} data-testid="global-search-results">
      {tiles.map(({ key, title, rows }) => (
        <section
          key={key}
          css={tileCss}
          data-testid={`global-search-tile-${key}`}
          onScroll={isPagedType(key) ? onTileScroll(key) : undefined}
        >
          <h3 css={tileHeaderCss} title={title}>
            {title}
          </h3>
          {key === 'map-datasets' && rows.length === 0 && (
            <p css={tileEmptyCss}>
              {mapDatasetsLoading ? 'Loading…' : 'No datasets in this map'}
            </p>
          )}
          <ul css={listCss}>
            {rows.map(({ result, index }) => (
              <li
                key={`${key}-${result.id}`}
                data-index={index}
                data-testid="global-search-result"
                css={(theme: BirdiTheme) => css`
                  ${itemCss(theme)};
                  background: ${index === selectedIndex
                    ? theme.dove
                    : 'transparent'};
                `}
                onMouseEnter={() => onHighlight(index)}
              >
                <button
                  type="button"
                  css={itemMainCss}
                  onClick={() => onActivate(result)}
                >
                  {result.thumbnailUrl ? (
                    <img
                      src={result.thumbnailUrl}
                      alt=""
                      css={thumbCss}
                      onError={(event) => {
                        // A cover that fails to load reads worse than none.
                        event.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <span css={typeDotCss(result.type)} />
                  )}
                  <span css={itemTextCss}>
                    {/* Names ellipsise; the tooltip carries the full name. */}
                    <span css={itemNameCss} title={result.name}>
                      {result.name}
                    </span>
                    <span css={itemMetaCss}>
                      <span
                        css={workspaceCss}
                        data-testid="global-search-workspace"
                      >
                        {result.workspaceName ?? 'Workspace'}
                      </span>
                      <span data-testid="global-search-date">
                        {formatDate(result.createdAt)}
                      </span>
                      {result.sizeBytes !== null && (
                        <span
                          css={sizePillCss}
                          data-testid="global-search-size-pill"
                        >
                          {formatBytes(result.sizeBytes)}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
                {(result.center || result.bbox) && (
                  <button
                    type="button"
                    title="Zoom to"
                    aria-label={`Zoom to ${result.name}`}
                    data-testid="global-search-zoom-to"
                    css={iconButtonCss}
                    onClick={() => onZoomTo(result)}
                  >
                    <CrosshairSVG />
                  </button>
                )}
                {/* Maps and missions link out; datasets link to their
                      parent map (when they have one). */}
                {(result.type !== 'dataset' || result.mapId) && (
                  <button
                    type="button"
                    title={GO_TO_LABELS[result.type]}
                    aria-label={`${GO_TO_LABELS[result.type]}: ${result.name}`}
                    data-testid="global-search-go-to"
                    css={iconButtonCss}
                    onClick={() => onGoToMap(result)}
                  >
                    <ExternalLinkSVG />
                  </button>
                )}
                {/* Download is a dataset action only (PRD). */}
                {result.type === 'dataset' && (
                  <button
                    type="button"
                    title="Download"
                    aria-label={`Download ${result.name}`}
                    data-testid="global-search-download"
                    css={iconButtonCss}
                    onClick={() => onDownload(result)}
                  >
                    <DownloadSVG />
                  </button>
                )}
              </li>
            ))}
          </ul>
          {isPagedType(key) && paging[key].loadingMore && (
            <p css={footerCss} data-testid={`global-search-loading-${key}`}>
              Loading more…
            </p>
          )}
        </section>
      ))}
    </div>
  );
};

/* The column holding the tiles: it does not scroll itself — each tile does
   (PRD), sharing the height. */
const columnCss = css`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

/* Paper card treatment — one tile per result type. Tiles split the column
   evenly (flex-basis 0, so pages appending inside a tile never change the
   split) but a short tile only takes what its content needs (max-height:
   max-content) and hands the rest to the others; a full tile scrolls on
   its own under a sticky title and pulls the next page in as you go. */
const tileCss = (theme: BirdiTheme) => css`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  max-height: max-content;
  background: ${theme.mono0};
  border: 1px solid ${theme.dove};
  border-radius: 5px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  overflow-y: auto;

  /* The title has overflow:hidden for its ellipsis, which lets flex shrink
     it to nothing when the list overflows — pin its height. */
  > h3 {
    flex: 0 0 auto;
    position: sticky;
    top: 0;
    background: ${theme.mono0};
    z-index: 1;
  }
  > p,
  > ul {
    flex: 0 0 auto;
  }
`;

const footerCss = (theme: BirdiTheme) => css`
  margin: 0;
  padding: 0.25rem 1rem 0.75rem;
  color: ${theme.mono40};
  font-size: 0.875rem;
`;

const tileHeaderCss = (theme: BirdiTheme) => css`
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0.6rem 1rem 0.35rem;
  color: ${theme.mono40};
  font-family: ${fonts.body};
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const tileEmptyCss = (theme: BirdiTheme) => css`
  margin: 0;
  padding: 0.25rem 1rem 0.75rem;
  color: ${theme.mono40};
  font-size: 0.875rem;
`;

const listCss = css`
  list-style: none;
  margin: 0;
  padding: 0 0 0.5rem;
`;

const itemCss = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0 0.5rem 0 0;
  border-radius: 6px;
  margin: 0 0.5rem;
  color: ${theme.mono70};
`;

const itemMainCss = css`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex: 1;
  min-width: 0;
  padding: 0.4rem 0.5rem;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
`;

const thumbCss = css`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

const TYPE_COLORS: Record<GlobalSearchResultType, string> = {
  map: 'hsl(203, 100%, 38%)',
  mission: 'hsl(150, 45%, 42%)',
  dataset: 'hsl(35, 85%, 55%)',
};

const typeDotCss = (type: GlobalSearchResultType) => css`
  width: 10px;
  height: 10px;
  margin: 0 11px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${TYPE_COLORS[type]};
`;

const itemTextCss = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const itemNameCss = css`
  font-size: 1rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const itemMetaCss = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.15rem;
  font-size: 0.875rem;
  color: ${theme.mono40};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* Workspace as plain meta text, separated from the date by a dot. */
const workspaceCss = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 11rem;

  &::after {
    content: '·';
    margin-left: 0.4rem;
  }
`;

/* Storage size as a small pill so it scans as a tag. */
const sizePillCss = (theme: BirdiTheme) => css`
  display: inline-block;
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
  background: ${theme.dove};
  color: ${theme.mono70};
  font-size: 0.75rem;
  line-height: 1.3;
  white-space: nowrap;
`;

const iconButtonCss = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: none;
  color: ${theme.mono70};
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${theme.dove};
  }
`;

const emptyCss = (theme: BirdiTheme) => css`
  padding: 2rem 1.5rem;
  color: ${theme.mono40};
  font-size: 1rem;
  text-align: center;
`;
